import { TextBase } from '@carboncopyco/ui';
import { useEffect, useMemo } from 'react';
import type { ImageDetails } from '../ImageInput';
import { ImageInput } from '../ImageInput';
import { InputField, type InputFieldProps } from '../InputField';
import { useImageUrlDetails } from '../hooks';

export interface SourceImageFieldProps {
  imageDetails?: ImageDetails;
  onChangeImageDetails?: (details: ImageDetails) => void;
  errorMessage?: string;
  onLearnMoreClick?: (url: string) => void;
}

const helpLink = 'https://help.demostack.com/en/articles/5919246';

export const SourceImageField = ({
  imageDetails,
  onChangeImageDetails,
  errorMessage,
  onLearnMoreClick,
}: SourceImageFieldProps) => {
  const [imageDetailsCalculated, status] = useImageUrlDetails(imageDetails);
  const imageFormatNotSupported = useMemo(
    () =>
      status === 'error' ||
      (imageDetailsCalculated && !imageDetailsCalculated.metadata.format),
    [status, imageDetailsCalculated]
  );

  useEffect(() => {
    onChangeImageDetails?.(
      imageFormatNotSupported ? null : imageDetailsCalculated
    );
  }, [imageDetailsCalculated, onChangeImageDetails, imageFormatNotSupported]);

  if (errorMessage) {
    return (
      <SourceInputField error={errorMessage}>
        <ImageInput
          disableFocus
          value={imageDetailsCalculated}
          state="error"
          placeholder={<SourceImagePlaceholder />}
        />
      </SourceInputField>
    );
  }

  if (imageFormatNotSupported) {
    return (
      <SourceInputField
        error={
          <>
            <span>Image format is not supported. </span>
            <a
              className="underline"
              href={helpLink}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                if (onLearnMoreClick) {
                  e.preventDefault();
                  onLearnMoreClick(helpLink);
                }
              }}
            >
              Learn more
            </a>
          </>
        }
      >
        <ImageInput
          disableFocus
          state="error"
          placeholder={<SourceImagePlaceholder />}
        />
      </SourceInputField>
    );
  }

  return (
    <SourceInputField>
      <ImageInput
        disableFocus
        value={imageDetailsCalculated}
        state={status}
        placeholder={<SourceImagePlaceholder />}
      />
    </SourceInputField>
  );
};

const SourceInputField = ({
  children,
  error,
}: Pick<InputFieldProps, 'children' | 'error'>) => (
  <InputField
    label="Find"
    name="sourceImage"
    error={error}
  >
    {children}
  </InputField>
);

const SourceImagePlaceholder = () => (
  <TextBase
    label="Select the image you would like to replace."
    textAlign="text-center"
  />
);
